import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Instagram, Facebook } from "lucide-react";
import { fetchOffers, mediaUrl } from "@/lib/store";

const PlatIcon = ({ p }) => p === "facebook" ? <Facebook className="h-3.5 w-3.5" /> : <Instagram className="h-3.5 w-3.5" />;

const Card = ({ o, center, eager, onOpen }) => {
  const img = mediaUrl(o.image);
  return (
    <button
      data-testid={`offer-card-${o.id}`}
      onClick={() => onOpen(o)}
      className={`group flex w-full flex-col overflow-hidden rounded-2xl border bg-white text-left transition duration-500 ${center ? "border-neutral-300 shadow-lg" : "border-neutral-200"}`}
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-neutral-100">
        {img ? (
          <img src={img} alt={o.title || "Promozione"} loading={eager ? "eager" : "lazy"} decoding="async"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm text-neutral-400">Locandina</div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        {o.title && <h3 className="line-clamp-2 text-base font-bold tracking-tight text-neutral-900 sm:text-lg">{o.title}</h3>}
        {o.ends_at && (
          <span className="inline-flex w-fit items-center rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-600">
            In promo fino al {new Date(o.ends_at).toLocaleDateString("it-IT")}
          </span>
        )}
        {o.desc && <p className="line-clamp-2 text-sm text-neutral-500">{o.desc}</p>}
        {o.cta_url && (
          <span className="mt-auto inline-flex items-center gap-1.5 pt-2 text-xs font-semibold text-neutral-800">
            <PlatIcon p={o.source_platform} /> Vedi su {o.source_platform === "facebook" ? "Facebook" : "Instagram"} <ExternalLink className="h-3 w-3" />
          </span>
        )}
      </div>
    </button>
  );
};

export const OffersCarousel = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);

  useEffect(() => { fetchOffers().then((d) => setItems(d.items || [])).catch(() => {}); }, []);

  const n = items.length;
  const go = useCallback((d) => setActive((v) => (v + d + n) % n), [n]);

  useEffect(() => {
    if (n < 2 || paused) return;
    const t = setInterval(() => setActive((v) => (v + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n, paused]);

  // preload immagine successiva
  useEffect(() => {
    if (n < 2) return;
    const nxt = mediaUrl(items[(active + 1) % n]?.image);
    if (nxt) { const im = new Image(); im.src = nxt; }
  }, [active, items, n]);

  if (!n) return null;

  const open = (o) => { if (o.cta_url) window.open(o.cta_url, "_blank", "noopener,noreferrer"); };
  const prev = (active - 1 + n) % n;
  const next = (active + 1) % n;

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <section data-testid="offers-carousel" className="border-y border-slate-200/80 bg-gradient-to-b from-blue-50/30 to-slate-50">
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6">
        <span className="font-mono text-xs font-semibold uppercase tracking-widest text-blue-600">Offerte &amp; Promozioni</span>
        <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Le nostre promozioni</h2>
      </div>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <div className="relative flex items-stretch justify-center">
          {n > 1 && (
            <button data-testid="offers-prev" onClick={() => go(-1)} aria-label="Precedente"
              className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-2 text-slate-600 shadow-sm backdrop-blur transition hover:border-blue-400 hover:text-blue-600">
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          {/* Mobile: 1 locandina + swipe */}
          <div className="w-full max-w-sm sm:hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div key={active} className="animate-in fade-in duration-500">
              <Card o={items[active]} center eager onOpen={open} />
            </div>
          </div>

          {/* Desktop: 3 card, centrale evidenziata */}
          <div className="hidden w-full items-center justify-center gap-5 px-12 sm:flex">
            {n > 2 && (
              <div className="w-1/4 shrink-0 scale-90 opacity-60 transition duration-500 hover:opacity-90">
                <Card o={items[prev]} onOpen={open} />
              </div>
            )}
            <div key={active} className="w-2/4 shrink-0 animate-in fade-in duration-500">
              <Card o={items[active]} center eager onOpen={open} />
            </div>
            {n > 1 && next !== active && (
              <div className="w-1/4 shrink-0 scale-90 opacity-60 transition duration-500 hover:opacity-90">
                <Card o={items[next]} eager onOpen={open} />
              </div>
            )}
          </div>

          {n > 1 && (
            <button data-testid="offers-next" onClick={() => go(1)} aria-label="Successivo"
              className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-2 text-slate-600 shadow-sm backdrop-blur transition hover:border-blue-400 hover:text-blue-600">
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>

        {n > 1 && (
          <div className="mt-5 flex justify-center gap-1.5">
            {items.map((_, k) => (
              <button key={k} data-testid={`offers-dot-${k}`} onClick={() => setActive(k)} aria-label={`Vai a ${k + 1}`}
                className={`h-1.5 rounded-full transition-all ${k === active ? "w-5 bg-blue-600" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
